// Bridges the call-center account list (which carries the trimmed
// `CallCenterAccount` shape) to the existing user-lifecycle components
// (#1261). Those components require a full `User`, so we load it by id and
// render the requested action once available. Reuses the existing
// deactivate / reactivate / reset-password flows verbatim — no new
// lifecycle logic.
import { useTranslation } from 'react-i18next';
import { useUser } from '@magimp/shared-hooks';
import { LoadingState, ErrorState } from '../../ui/index.js';
import { errorMessage } from '../../api/errors.js';
import {
  UserDeactivateConfirm,
  UserReactivateConfirm,
  UserResetPasswordModal,
} from '../../account/index.js';

export type AccountLifecycleKind = 'deactivate' | 'reactivate' | 'reset';

export interface AccountLifecycleActionProps {
  userId: string;
  kind: AccountLifecycleKind;
  onClose: () => void;
}

export function AccountLifecycleAction({ userId, kind, onClose }: AccountLifecycleActionProps) {
  const { t } = useTranslation(['common']);
  const userQ = useUser(userId);

  if (userQ.isPending) return <LoadingState text={t('common:states.loading')} />;
  if (userQ.isError || !userQ.data) {
    return <ErrorState title={t('common:states.errorTitle')} text={errorMessage(userQ.error)} />;
  }
  const user = userQ.data;
  if (kind === 'deactivate') return <UserDeactivateConfirm user={user} onClose={onClose} />;
  if (kind === 'reactivate') return <UserReactivateConfirm user={user} onClose={onClose} />;
  return <UserResetPasswordModal user={user} onClose={onClose} />;
}
